/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { useState, useEffect } from 'react'
import cx from 'classnames'

import type { EuiAccordionProps } from '@elastic/eui'
import { EuiAccordion, useEuiTheme } from '@elastic/eui'

import type { FunctionComponent } from 'react'

// Styles
const accordionStyles = css({
  '.euiAccordion__triggerWrapper': {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
  '&.euiAccordion-isOpen': {
    '.cuiAccordionButton': {
      alignSelf: 'center',
    },
  },
})

export const CuiAccordion: FunctionComponent<EuiAccordionProps> = ({
  className,
  buttonClassName,
  children,
  ...props
}) => {
  const { euiTheme } = useEuiTheme()
  const [isSmallScreen, setIsSmallScreen] = useState(false)

  const buttonStyles = css({
    paddingLeft: euiTheme.size.s, // $euiSizeS
    color: euiTheme.colors.primary, // $euiColorPrimary
    flex: '0 1 0',
  })

  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth <= 360
      setIsSmallScreen(smallScreen)
    }

    // Initial check
    handleResize()

    // Add event listener
    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <EuiAccordion
      className={className}
      css={accordionStyles}
      {...props}
      buttonClassName={cx(!isSmallScreen ? 'eui-textNoWrap' : undefined, buttonClassName)}
      buttonProps={{
        ...props.buttonProps,
        css: buttonStyles,
      }}
    >
      {children}
    </EuiAccordion>
  )
}
